import React, { useState, useMemo, useCallback } from "react";
import { Outlet, useNavigate } from "react-router";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InsertChartOutlinedOutlinedIcon from "@mui/icons-material/InsertChartOutlinedOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import SignLanguageIcon from "@mui/icons-material/SignLanguage";
// import { clarity } from "react-microsoft-clarity";
import { SessionContext } from "./context/SessionContext";
import theme from "./theme";

// Define branding for the app
const BRANDING = {
  title: "Smart Voice",
  logo: (
    <img
      src="https://www.dsf.ir/wp-content/uploads/2024/07/cropped-IMG_20240418_120705_537-192x192.jpg"
      alt="MUI logo"
    />
  ),
};

function App() {
  // clarity.init("qifeey0smx");

  const [session, setSession] = useState(null);

  const navigate = useNavigate();

  const signIn = useCallback(() => {
    navigate("/sign-in");
  }, [navigate]);

  const signOut = useCallback(() => {
    setSession(null);
    navigate("/sign-in");
  }, [navigate]);

  const sessionContextValue = useMemo(
    () => ({ session, setSession }),
    [session, setSession],
  );

  const NAVIGATION = useMemo(() => {
    if (!session) return [];

    if (session.role === "player") {
      return [
        {
          kind: "header",
          title: "سرویس ها",
        },
        {
          title: "مترجم زبان اشاره",
          segment: "dashboard/sign-translator",
          icon: <SignLanguageIcon />,
        },
        {
          title: "اطلاعات  شخصی",
          segment: "dashboard",
          icon: <AccountBoxIcon />,
        },
        {
          title: "برنامه هفتگی",
          segment: "dashboard/train",
          icon: <FitnessCenterIcon />,
        },
      ];
    }

    if (session.role === "coach") {
      return [
        {
          kind: "header",
          title: "سرویس ها",
        },
        {
          title: "اطلاعات  شخصی",
          segment: "dashboard",
          icon: <AccountBoxIcon />,
        },
        {
          title: "مترجم زبان اشاره",
          segment: "dashboard/sign-translator",
          icon: <SignLanguageIcon />,
        },
        {
          title: "برنامه اردو",
          segment: "dashboard/add-schedule",
          icon: <CalendarMonthIcon />,
          pattern: "dashboard/add-schedule{/:id}*",
        },
      ];
    }

    if (session.role === "admin") {
      return [
        { title: "ادمین", segment: "dashboard/admin" },
        {
          title: "مترجم زبان اشاره",
          segment: "dashboard/sign-translator",
          icon: <SignLanguageIcon />,
        },
      ];
    }

    if (session.role === "manager") {
      return [
        {
          kind: "header",
          title: "سرویس ها",
        },
        {
          title: "اطلاعات  شخصی",
          segment: "dashboard",
          icon: <AccountBoxIcon />,
        },
        {
          title: "مترجم زبان اشاره",
          segment: "dashboard/sign-translator",
          icon: <SignLanguageIcon />,
        },
        {
          kind: "header",
          title: "برنامه اردو ها",
        },
        {
          title: "مشاهده اطلاعات اردو ها",
          segment: "dashboard/view-camps-info",
          icon: <ViewCompactIcon />,
        },
        {
          title: "مشاهده اردو های فعال",
          segment: "dashboard/view-camp",
          icon: <CalendarMonthOutlinedIcon />,
        },
        {
          title: "اضافه کردن اردو",
          segment: "dashboard/test",
          icon: <MoreTimeOutlinedIcon />,
        },
        {
          title: "کادر فنی",
          segment: "dashboard/technical-staff",
          icon: <GroupsOutlinedIcon />,
        },
        {
          kind: "header",
          title: "گزارشات",
        },
        {
          title: "تمرینات",
          segment: "dashboard/train-report",
          icon: <InsertChartOutlinedOutlinedIcon />,
        },
        {
          title: "تیم",
          segment: "dashboard/team-report",
          icon: <Diversity3OutlinedIcon />,
        },
        {
          title: "عملکرد بازیکنان",
          segment: "dashboard/players-report",
          icon: <PersonSearchOutlinedIcon />,
        },
        {
          kind: "header",
          title: "آنالیز بازی ها",
        },
        {
          title: "آنالیز بازی های فوتبال",
          segment: "dashboard/test1",
          icon: <SportsSoccerOutlinedIcon />,
        },
      ];
    }

    return [];
  }, [session]);

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <ReactRouterAppProvider
        navigation={NAVIGATION}
        branding={BRANDING}
        authentication={{ signIn, signOut }}
        session={session}
        theme={theme}
      >
        {/* Outlet renders the matched child route */}
        <Outlet />
      </ReactRouterAppProvider>
    </SessionContext.Provider>
  );
}

export default App;
